import { useFunctionsGatsby } from "@hooks/useFunctionsGatsby";
import { useKlaviyo } from "~/hooks/useKlaviyo";
import { ComponentProps } from "@ts/components";
import { type Content } from "@ts/sanity";
import React, {
  useState,
  type ChangeEvent,
  type FC,
  type FormEvent,
} from "react";
import { z } from "zod";

type ErrorMap = {
  [key: string]: string;
};

type QuestionsFormInputProps = ComponentProps & {
  sku?: string;
  productUrl?: string;
};

type QuestionsFormOutputProps = QuestionsFormInputProps & {
  loading?: boolean;
  success?: boolean;
  data: {
    name: string;
    email: string;
    content: string;
  };
  errors: ErrorMap;
  handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
};

const schema = z.object({
  email: z
    .string()
    .email("Email is invalid")
    .nonempty("This field is required"),
  name: z.string().nonempty("This field is required"),
  content: z.string().nonempty("This field is required"),
});

export const withQuestionsForm =
  (Component: FC<QuestionsFormOutputProps>) =>
  ({
    name = "QuestionsForm",
    sku,
    productUrl,
    className,
  }: QuestionsFormInputProps) => {
    const { productQuestionAdd, loading } = useFunctionsGatsby();
    const klaviyo = useKlaviyo();

    const [data, setData] = useState({
      name: "",
      email: "",
      content: "",
    });
    const [success, setSuccess] = useState(false);
    const [errors, setErrors] = useState<ErrorMap>({});

    const handleChange = ({ target: { name, value } }) => {
      setData((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();

      const result = schema.safeParse(data);

      if (!result.success) {
        const mappedErrors = result.error.issues.reduce((map, issue) => {
          return {
            ...map,
            [issue.path[0] ?? "unknown"]: issue.message,
          };
        }, {});

        return setErrors(mappedErrors);
      }
      setErrors({});

      klaviyo.track("Review Ask", {
        email: data.email,
        grouping_hash: sku,
        name: data.name,
        question: data.content,
        url: productUrl,
      });

      const { status } = await productQuestionAdd(
        data.email,
        sku,
        data.name,
        data.content,
        productUrl,
      );
      setSuccess(status === "success");
    };

    Component.displayName = name;
    return (
      <Component
        data={data}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        className={className}
        loading={loading}
        errors={errors}
        success={success}
      />
    );
  };
